import { useEffect, useState } from "react";
import { Building2, Briefcase, ShieldCheck } from "lucide-react";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs/tabs.tsx";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import {
  DepartmentsTab,
  DesignationsTab,
  OrganizationStructureHero,
  ORGANIZATION_STRUCTURE_TABS,
  RolesTab,
  TAB_DEPARTMENTS,
  TAB_DESIGNATIONS,
  TAB_ROLES,
  type OrganizationStructureTabId,
} from "../../components/features/organization-structure/index.ts";
import type { PageProps } from "../../types/page.ts";

import styles from "./organization-structure-page.module.css";

const TAB_ICONS: Record<OrganizationStructureTabId, typeof Building2> = {
  departments: Building2,
  designations: Briefcase,
  roles: ShieldCheck,
};

export default function OrganizationStructurePage({
  title,
  description,
  pageMeta,
}: PageProps) {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [activeTab, setActiveTab] =
    useState<OrganizationStructureTabId>(TAB_DEPARTMENTS);
  const [departmentsRefreshKey, setDepartmentsRefreshKey] = useState(0);

  const pageTitle = pageMeta?.title || title || "Organization Structure";
  const pageDescription =
    pageMeta?.description ||
    description ||
    "Manage departments, designations, and roles to build a strong organizational structure and define reporting relationships.";


const heroTitle = "Organization Management";

const heroDescription =
  "Manage departments, designations, and roles within your organization.";

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, pageTitle]);

  return (
    <PageShell>
      <OrganizationStructureHero
        title={heroTitle}
        description={heroDescription}
      />
 

      <Card className={styles.tableCard} padding="5xl">
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            setActiveTab(value as OrganizationStructureTabId)
          }
          variant="card"
        >
          <TabsList className={styles.tabsList}>
            {ORGANIZATION_STRUCTURE_TABS.map((tab) => {
              const Icon = TAB_ICONS[tab.id];
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className={styles.tabTrigger}
                >
                  <span className={styles.tabTriggerInner}>
                    <Icon size={16} aria-hidden />
                    {tab.label}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value={TAB_DEPARTMENTS} className={styles.tabPanel}>
            <DepartmentsTab
              onDepartmentsChanged={() =>
                setDepartmentsRefreshKey((value) => value + 1)
              }
            />
          </TabsContent>
          <TabsContent value={TAB_DESIGNATIONS} className={styles.tabPanel}>
            <DesignationsTab departmentsRefreshKey={departmentsRefreshKey} />
          </TabsContent>
          <TabsContent value={TAB_ROLES} className={styles.tabPanel}>
            <RolesTab />
          </TabsContent>
        </Tabs>
      </Card>
    </PageShell>
  );
}
